import React, { useState, useRef } from "react";
import Toolbar from "../components/designer/Toolbar";
import BrushLibrary from "../components/designer/BrushLibrary";
import DesignsPanel from "../components/designer/DesignsPanel";
import LayersPanel from "../components/designer/LayersPanel";
import CanvasStage from "../components/designer/CanvasStage";
const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const AUTH_TOKEN_KEY = 'ivisora_auth_token';

const defaultBrushes = [
  { id: "pencil", name: "Pencil", size: 4, color: "#111827", opacity: 1, hardness: 0.9 },
  { id: "marker", name: "Marker", size: 12, color: "#1f2937", opacity: 0.9, hardness: 0.7 },
  { id: "soft", name: "Soft Brush", size: 28, color: "#111827", opacity: 0.5, hardness: 0.2 },
  { id: "hard", name: "Hard Brush", size: 18, color: "#111827", opacity: 1, hardness: 1 },
  { id: "calligraphy", name: "Calligraphy", size: 20, color: "#111827", opacity: 1, hardness: 0.8 }
];

export default function DesignerPage() {
  const [brushes, setBrushes] = useState(defaultBrushes);
  const [activeBrushId, setActiveBrushId] = useState(defaultBrushes[0].id);
  const [layers, setLayers] = useState([
    { id: "layer-1", name: "Layer 1", visible: true, locked: false, strokes: [], mask: null },
  ]);
  const [activeLayerId, setActiveLayerId] = useState("layer-1");
  const [selectedLayerIds, setSelectedLayerIds] = useState([activeLayerId]);

  const activeBrush = brushes.find((b) => b.id === activeBrushId) || brushes[0];
  const [designId, setDesignId] = useState(null);
  const [designName, setDesignName] = useState('Untitled Design');
  const [submissionStatus, setSubmissionStatus] = useState(null); // 'pending'|'approved'|'rejected'|'published'|null
  const [saveLoading, setSaveLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  const addStroke = (stroke) => {
    // Create a new layer for each stroke (auto new-layer-on-stroke)
    const newLayerId = "layer-" + Date.now();
    const newLayer = {
      id: newLayerId,
      name: `Layer ${layers.length + 1}`,
      visible: true,
      locked: false,
      strokes: [stroke],
      mask: null,
    };
    setLayers((prev) => {
      const next = [...prev, newLayer];
      // push history action
      pushAction({ type: "addLayer", layer: newLayer });
      return next;
    });
    setActiveLayerId(newLayerId);
  };

  const addLayer = () => {
    const newLayerId = "layer-" + Date.now();
    const newLayer = {
      id: newLayerId,
      name: `Layer ${layers.length + 1}`,
      visible: true,
      locked: false,
      strokes: [],
      mask: null,
    };
    setLayers((prev) => {
      pushAction({ type: "addLayer", layer: newLayer });
      return [...prev, newLayer];
    });
    setActiveLayerId(newLayerId);
  };

  // load design into editor (from local or remote)
  const loadDesign = (design) => {
    try {
      const pages = design.pages || [];
      // for simplicity set layers from pages elements
      const newLayers = pages.map((p, i) => ({
        id: p.id || `layer-${i}-${Date.now()}`,
        name: p.name || `Page ${i+1}`,
        visible: true,
        locked: false,
        strokes: p.elements || [],
        mask: null
      }));
      if (newLayers.length === 0) {
        // fallback: empty layer
        setLayers([{ id: 'layer-1', name: 'Layer 1', visible: true, locked: false, strokes: [], mask: null }]);
      } else {
        setLayers(newLayers);
        setActiveLayerId(newLayers[newLayers.length - 1].id);
      }
      setDesignId(design.id || null);
      setDesignName(design.name || 'Untitled Design');
      setSubmissionStatus(design.submission_status || null);
    } catch (e) {
      console.error('Failed to load design', e);
    }
  };

  const deleteActiveLayer = () => {
    setLayers((prev) => {
      const idx = prev.findIndex((l) => l.id === activeLayerId);
      if (idx === -1) return prev;
      const layer = prev[idx];
      const next = prev.filter((l) => l.id !== activeLayerId);
      pushAction({ type: "deleteLayer", layer, index: idx });
      // choose new active
      const nextActive = next.length ? next[next.length - 1].id : null;
      setActiveLayerId(nextActive);
      return next;
    });
  };

  // History stacks for undo/redo
  const undoStackRef = useRef([]);
  const redoStackRef = useRef([]);

  const pushAction = (action) => {
    undoStackRef.current.push(action);
    // clear redo
    redoStackRef.current = [];
  };

  const undo = () => {
    const action = undoStackRef.current.pop();
    if (!action) return;
    // handle inverse
    if (action.type === "addLayer") {
      setLayers((prev) => prev.filter((l) => l.id !== action.layer.id));
      redoStackRef.current.push(action);
    } else if (action.type === "deleteLayer") {
      // restore at index
      setLayers((prev) => {
        const copy = [...prev];
        copy.splice(action.index, 0, action.layer);
        return copy;
      });
      redoStackRef.current.push(action);
    } else if (action.type === "addStroke") {
      setLayers((prev) =>
        prev.map((l) => (l.id === action.layerId ? { ...l, strokes: l.strokes.filter((s) => s.id !== action.stroke.id) } : l))
      );
      redoStackRef.current.push(action);
    } else if (action.type === "applyMask") {
      setLayers((prev) => prev.map((l) => (l.id === action.bottomId ? { ...l, mask: action.prevMask } : l)));
      redoStackRef.current.push(action);
    }
  };

  const redo = () => {
    const action = redoStackRef.current.pop();
    if (!action) return;
    if (action.type === "addLayer") {
      setLayers((prev) => [...prev, action.layer]);
      undoStackRef.current.push(action);
    } else if (action.type === "deleteLayer") {
      setLayers((prev) => prev.filter((l) => l.id !== action.layer.id));
      undoStackRef.current.push(action);
    } else if (action.type === "addStroke") {
      setLayers((prev) => prev.map((l) => (l.id === action.layerId ? { ...l, strokes: [...l.strokes, action.stroke] } : l)));
      undoStackRef.current.push(action);
    } else if (action.type === "applyMask") {
      setLayers((prev) => prev.map((l) => (l.id === action.bottomId ? { ...l, mask: action.newMask } : l)));
      undoStackRef.current.push(action);
    }
  };

  // Toggle selection with ctrl/meta for multi-select
  const toggleSelectLayer = (layerId, e) => {
    const isMulti = e && (e.ctrlKey || e.metaKey || e.shiftKey);
    setSelectedLayerIds((prev) => {
      if (!isMulti) return [layerId];
      if (prev.includes(layerId)) return prev.filter((id) => id !== layerId);
      return [...prev, layerId];
    });
  };

  // compute convex hull (monotone chain) for mask polygon from strokes' points
  const computeHullFromPoints = (points) => {
    if (!points || points.length < 6) return [];
    // points is [x,y,x,y,...] convert to array of [x,y]
    const pts = [];
    for (let i = 0; i < points.length; i += 2) pts.push([points[i], points[i + 1]]);

    // remove duplicates
    const uniq = Array.from(new Set(pts.map(p => p[0]+','+p[1]))).map(s => s.split(',').map(Number));
    if (uniq.length < 3) return [];

    uniq.sort((a,b) => a[0] === b[0] ? a[1]-b[1] : a[0]-b[0]);
    const cross = (o,a,b) => (a[0]-o[0])*(b[1]-o[1]) - (a[1]-o[1])*(b[0]-o[0]);
    const lower = [];
    for (const p of uniq) {
      while (lower.length >= 2 && cross(lower[lower.length-2], lower[lower.length-1], p) <= 0) lower.pop();
      lower.push(p);
    }
    const upper = [];
    for (let i = uniq.length-1; i >=0; i--) {
      const p = uniq[i];
      while (upper.length >= 2 && cross(upper[upper.length-2], upper[upper.length-1], p) <= 0) upper.pop();
      upper.push(p);
    }
    upper.pop();
    lower.pop();
    const hull = lower.concat(upper);
    // flatten to [x,y,...]
    const flat = [];
    hull.forEach(p => { flat.push(p[0], p[1]); });
    return flat;
  };

  // Apply or release mask for two selected layers (top as mask for bottom)
  const applyMaskToSelection = () => {
    if (selectedLayerIds.length !== 2) {
      alert("Select exactly two layers to apply mask (top layer will be the mask).");
      return;
    }
    // determine order in layers array (bottom -> top)
    const idxs = selectedLayerIds.map(id => layers.findIndex(l => l.id === id));
    if (idxs.some(i => i === -1)) return;
    const [aIdx, bIdx] = idxs;
    let bottomIdx = Math.min(aIdx, bIdx);
    let topIdx = Math.max(aIdx, bIdx);
    if (bottomIdx === topIdx) return;
    const bottom = layers[bottomIdx];
    const top = layers[topIdx];

    // if bottom already masked by this top -> release
    if (bottom.mask && bottom.mask.sourceLayerId === top.id) {
      // release
      setLayers(prev => prev.map((l, i) => i===bottomIdx ? { ...l, mask: null } : l));
      return;
    }

    // collect all points from top layer strokes
    let allPoints = [];
    for (const s of top.strokes) {
      if (s.points && s.points.length) allPoints = allPoints.concat(s.points);
    }
    const hull = computeHullFromPoints(allPoints);
    if (!hull || hull.length < 6) {
      alert("Mask layer doesn't have enough geometry to create a mask. Draw a closed shape or larger strokes.");
      return;
    }

    // apply mask array to bottom layer
    const newMask = { type: "strokes", sourceLayerId: top.id, points: hull };
    setLayers(prev => {
      const updated = prev.map((l, i) => {
        if (i === bottomIdx) return { ...l, mask: newMask };
        if (i === topIdx) return { ...l, isMaskSource: true };
        return l;
      });
      pushAction({ type: "applyMask", bottomId: updated[bottomIdx].id, prevMask: prev[bottomIdx].mask || null, newMask });
      return updated;
    });
  };

  const updateLayer = (updated) => {
    setLayers((prev) => prev.map((l) => (l.id === updated.id ? { ...l, ...updated } : l)));
  };

  const releaseMask = (bottomLayerId) => {
    setLayers((prev) => {
      const idx = prev.findIndex((l) => l.id === bottomLayerId);
      if (idx === -1) return prev;
      const prevMask = prev[idx].mask || null;
      const updated = prev.map((l, i) => (i === idx ? { ...l, mask: null } : l.id === prev[idx].mask?.sourceLayerId ? { ...l, isMaskSource: false } : l));
      pushAction({ type: "applyMask", bottomId: bottomLayerId, prevMask, newMask: null });
      return updated;
    });
  };

  // Save design to backend or localStorage
  const saveDesign = async () => {
    setSaveLoading(true);
    const payload = {
      name: designName,
      canvas_size: { width: 1200, height: 800 },
      pages: layers.map((l) => ({ id: l.id, name: l.name, elements: l.strokes || [], background: '#ffffff' })),
      current_page_id: layers[0]?.id || null
    };

    try {
      if (!API_URL) {
        // save locally
        const design = { id: Date.now().toString(), ...payload, submission_status: null, updatedAt: new Date().toISOString() };
        const saved = JSON.parse(localStorage.getItem('designs') || '[]');
        saved.push(design);
        localStorage.setItem('designs', JSON.stringify(saved));
        setDesignId(design.id);
        setSubmissionStatus(design.submission_status);
        alert('Saved locally. Sign in to save to cloud.');
      } else {
        const token = localStorage.getItem(AUTH_TOKEN_KEY);
        const url = designId ? `${API_URL}/api/designs/${designId}` : `${API_URL}/api/designs`;
        const method = designId ? 'PUT' : 'POST';
        const res = await fetch(url, {
          method,
          headers: {
            'Content-Type': 'application/json',
            Authorization: token ? `Bearer ${token}` : undefined
          },
          body: JSON.stringify(payload)
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || 'Save failed');
        setDesignId(data.id || data.id);
        setSubmissionStatus(data.submission_status || null);
        alert('Saved to cloud');
      }
    } catch (err) {
      console.error(err);
      alert('Save failed: ' + err.message);
    } finally {
      setSaveLoading(false);
    }
  };

  const submitForApproval = async () => {
    if (!designId) {
      alert('Please save the design first.');
      return;
    }
    setSubmitLoading(true);
    try {
      if (!API_URL) {
        // local simulation
        const saved = JSON.parse(localStorage.getItem('designs') || '[]');
        const idx = saved.findIndex(d => d.id === designId);
        if (idx !== -1) {
          saved[idx].submission_status = 'pending';
          localStorage.setItem('designs', JSON.stringify(saved));
          setSubmissionStatus('pending');
          alert('Submitted for approval (local)');
        } else {
          alert('Design not found locally. Save first.');
        }
      } else {
        const token = localStorage.getItem(AUTH_TOKEN_KEY);
        if (!token) {
          alert('Sign in to submit for approval');
          setSubmitLoading(false);
          return;
        }
        const res = await fetch(`${API_URL}/api/designs/${designId}/submit`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || 'Submit failed');
        setSubmissionStatus('pending');
        alert('Submitted for approval');
      }
    } catch (err) {
      console.error(err);
      alert('Submit failed: ' + err.message);
    } finally {
      setSubmitLoading(false);
    }
  };

  // UI helpers
  const statusBadge = submissionStatus ? (
    <span className="px-2 py-1 rounded text-sm bg-yellow-100 text-yellow-800">{submissionStatus}</span>
  ) : null;

  return (
    <div className="min-h-screen flex flex-col">
      <Toolbar onApplyMask={applyMaskToSelection} onAddLayer={addLayer} onDeleteLayer={deleteActiveLayer} onUndo={undo} onRedo={redo} />
      <div className="flex items-center justify-between px-4 py-2 bg-white border-b">
        <div className="flex items-center gap-3">
          <input value={designName} onChange={(e) => setDesignName(e.target.value)} className="border px-2 py-1 rounded" />
          {statusBadge}
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={saveDesign} className="px-3 py-1 rounded bg-blue-600 text-white" disabled={saveLoading}>
            {saveLoading ? 'Saving...' : 'Save'}
          </button>
          <button type="button" onClick={submitForApproval} className="px-3 py-1 rounded bg-purple-600 text-white" disabled={submitLoading}>
            {submitLoading ? 'Submitting...' : 'Submit for approval'}
          </button>
        </div>
      </div>
      <div className="flex-1 flex overflow-hidden">
        <aside className="w-72 border-r bg-white/50 p-4">
          <BrushLibrary brushes={brushes} activeBrushId={activeBrushId} onSelect={setActiveBrushId} />
          <div className="mt-6">
            <DesignsPanel apiUrl={API_URL} onLoadDesign={loadDesign} />
          </div>
        </aside>
        <main className="flex-1 relative bg-gray-100 p-4">
          <CanvasStage
            width={1200}
            height={800}
            brush={activeBrush}
            layers={layers}
            onAddStroke={addStroke}
            onUpdateLayer={updateLayer}
            selectedLayerIds={selectedLayerIds}
          />
        </main>
        <aside className="w-80 border-l bg-white/50 flex flex-col" style={{ height: 'calc(100vh - 64px)' }}>
          {/* pad the sidebar but let the panel itself fill and scroll */}
          <div className="p-4 flex-1 overflow-hidden">
            <div className="h-full flex flex-col">
              <LayersPanel
                layers={layers}
                activeLayerId={activeLayerId}
                onSetActiveLayer={setActiveLayerId}
                onUpdateLayer={updateLayer}
                onToggleSelectLayer={toggleSelectLayer}
                selectedLayerIds={selectedLayerIds}
                onReleaseMask={releaseMask}
              />
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

